<template>
  <v-container>
    <v-card>
      <v-card-title class="d-flex flex-wrap align-center ga-3">
        <span>Dashboard de combustible</span>
        <v-spacer />
        <v-select v-model="meses" :items="periodos" label="Período" density="compact" hide-details
          style="max-width: 170px" />
        <v-autocomplete v-model="autoId" :items="autos" :item-title="a => `${a.placa} — ${a.marca} ${a.modelo}`"
          item-value="id" label="Todos los autos" clearable density="compact" hide-details style="min-width: 240px" />
      </v-card-title>
      <v-divider />

      <v-card-text>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
        <v-progress-linear v-if="cargando" indeterminate color="primary" class="mb-4" />

        <!-- Resumen: gasto del último mes cerrado y precio por galón de cada tipo -->
        <v-row class="mb-2">
          <v-col cols="12" sm="6" md="3">
            <v-card variant="outlined" class="h-100">
              <v-card-subtitle class="pt-3">Gasto {{ nombreMes(mesCerrado?.clave) }}</v-card-subtitle>
              <v-card-text>
                <div class="text-h5 font-weight-bold">{{ soles(mesCerrado?.gasto) }}</div>
                <Variacion :actual="mesCerrado?.gasto" :anterior="mesPrevio?.gasto"
                  :referencia="nombreMes(mesPrevio?.clave)" />
                <div class="text-caption text-medium-emphasis mt-1">
                  Mes en curso: {{ soles(mesActual?.gasto) }} hasta hoy
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card variant="outlined" class="h-100">
              <v-card-subtitle class="pt-3">Galones {{ nombreMes(mesCerrado?.clave) }}</v-card-subtitle>
              <v-card-text>
                <div class="text-h5 font-weight-bold">{{ galones(mesCerrado?.galones) }}</div>
                <Variacion :actual="mesCerrado?.galones" :anterior="mesPrevio?.galones"
                  :referencia="nombreMes(mesPrevio?.clave)" unidad="gal" />
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ mesCerrado?.cargas || 0 }} cargas
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-for="t in resumenTipos" :key="t.id" cols="12" sm="6" md="3">
            <v-card variant="outlined" class="h-100">
              <v-card-subtitle class="pt-3 d-flex align-center ga-2">
                <span class="muestra" :style="{ background: t.color }" />
                {{ t.tipo }} · por galón
              </v-card-subtitle>
              <v-card-text>
                <div class="text-h5 font-weight-bold">{{ t.actual ? soles(t.actual.precio_galon) : '—' }}</div>
                <Variacion :actual="t.actual?.precio_galon" :anterior="t.anterior?.precio_galon"
                  :referencia="nombreMes(t.anterior?.clave)" moneda />
                <div class="text-caption text-medium-emphasis mt-1">
                  Última carga: {{ nombreMes(t.actual?.clave) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="hayDatos">
          <div class="text-subtitle-1 font-weight-medium">Precio promedio por galón</div>
          <div class="text-caption text-medium-emphasis mb-2">
            Una gráfica por tipo de combustible, cada una con su propia escala para que se note si subió o bajó.
            Precio ponderado por los galones cargados en el mes; los meses sin cargas quedan en blanco.
          </div>
          <v-row class="mb-2">
            <v-col v-for="t in resumenTipos" :key="t.id" cols="12" :md="resumenTipos.length > 1 ? 6 : 12">
              <v-card variant="outlined">
                <v-card-title class="text-subtitle-2 d-flex align-center ga-2">
                  <span class="muestra" :style="{ background: t.color }" />
                  {{ t.tipo }}
                  <span class="text-medium-emphasis font-weight-regular">
                    · {{ t.actual ? soles(t.actual.precio_galon) : '—' }}
                  </span>
                </v-card-title>
                <v-card-text>
                  <div :ref="el => (precioEls[t.id] = el)" class="amchart amchart--mini" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1">
              Gasto mensual en combustible
              <div class="text-caption text-medium-emphasis">La última barra (más clara) es el mes en curso.</div>
            </v-card-title>
            <v-card-text>
              <div ref="gastoChart" class="amchart" />
            </v-card-text>
          </v-card>

          <v-card variant="outlined">
            <v-card-title class="text-subtitle-1">Detalle por mes</v-card-title>
            <v-data-table :headers="headersTabla" :items="filasTabla" density="compact" :items-per-page="-1"
              hide-default-footer class="tabular" />
          </v-card>
        </template>
        <v-alert v-else-if="!cargando" type="info" variant="tonal">
          No hay cargas de combustible en el período seleccionado.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { defineComponent, h, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { useTheme } from 'vuetify';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

// Paleta categórica validada (orden fijo; el color sigue al tipo de combustible, no a su posición).
const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};
const TINTA = {
  light: { texto: 0x52514e, grid: 0xe1e0d9, eje: 0xc3c2b7, superficie: 0xffffff },
  dark: { texto: 0xc3c2b7, grid: 0x2c2c2a, eje: 0x383835, superficie: 0x212121 },
};

const soles = (v) => (v == null ? '—' : Number(v).toLocaleString('es-PE', { style: 'currency', currency: 'PEN' }));
const galones = (v) => `${Number(v || 0).toLocaleString('es-PE', { maximumFractionDigits: 1 })} gal`;
const nombreMes = (clave) => {
  if (!clave) return '';
  const [a, m] = clave.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('es-PE', { month: 'short', year: 'numeric' });
};

// Para el eje X de las gráficas: "oct. 25"
const mesCorto = (clave) => {
  const [a, m] = clave.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('es-PE', { month: 'short', year: '2-digit' });
};

/** ▲/▼ con monto y %, siempre con ícono y texto. En costos, subir es malo (rojo) y bajar es bueno (verde). */
const Variacion = defineComponent({
  props: {
    actual: [Number, String],
    anterior: [Number, String],
    referencia: String,
    moneda: Boolean,
    unidad: String,
  },
  setup(props) {
    return () => {
      const a = Number(props.actual);
      const b = Number(props.anterior);
      if (props.actual == null || props.anterior == null || !b) {
        return h('div', { class: 'text-caption text-medium-emphasis' }, 'Sin mes anterior para comparar');
      }
      const diff = a - b;
      const pct = (diff / b) * 100;
      const igual = Math.abs(pct) < 0.05;
      const sube = diff > 0;
      // Para galones no hay "bueno" o "malo": se muestra neutro.
      const color = igual || props.unidad ? 'text-medium-emphasis' : (sube ? 'text-error' : 'text-success');
      const icono = igual ? 'mdi-minus' : (sube ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold');
      const monto = props.unidad
        ? `${Math.abs(diff).toLocaleString('es-PE', { maximumFractionDigits: 1 })} ${props.unidad}`
        : soles(Math.abs(diff));
      const texto = igual
        ? `Igual que ${props.referencia}`
        : `${sube ? 'Subió' : 'Bajó'} ${monto} (${sube ? '+' : '−'}${Math.abs(pct).toFixed(1)}%) vs ${props.referencia}`;
      return h('div', { class: `d-flex align-center ga-1 text-body-2 font-weight-medium ${color}` }, [
        h('i', { class: `mdi ${icono}`, 'aria-hidden': 'true' }),
        h('span', texto),
      ]);
    };
  },
});

export default defineComponent({
  components: { Variacion },
  setup() {
    const store = useStore();
    const theme = useTheme();
    const isDark = computed(() => theme.global.current.value.dark);
    const modo = computed(() => (isDark.value ? 'dark' : 'light'));

    const periodos = [
      { title: 'Últimos 6 meses', value: 6 },
      { title: 'Últimos 12 meses', value: 12 },
      { title: 'Últimos 24 meses', value: 24 },
    ];
    const meses = ref(12);
    const autoId = ref(null);
    const datos = ref({ meses: [], por_tipo: [] });
    const cargando = ref(false);
    const error = ref(null);

    const precioEls = {};
    const gastoChart = ref(null);
    let raices = [];

    const autos = computed(() => store.getters['gasto/autos']);
    const hayDatos = computed(() => datos.value.meses.some((m) => m.cargas > 0));

    // El último mes del rango es el mes en curso (incompleto): se compara el último mes cerrado.
    const mesActual = computed(() => datos.value.meses.at(-1));
    const mesCerrado = computed(() => datos.value.meses.at(-2));
    const mesPrevio = computed(() => datos.value.meses.at(-3));

    const colorTipo = (i) => SERIES[modo.value][i % SERIES[modo.value].length];

    // Precio por tipo: último mes con cargas vs el mes con cargas anterior a ese.
    const resumenTipos = computed(() => datos.value.por_tipo.map((t, i) => {
      const conDatos = t.serie.filter((p) => p.precio_galon != null);
      return {
        id: t.tipo_combustible_id,
        tipo: t.tipo,
        color: colorTipo(i),
        actual: conDatos.at(-1),
        anterior: conDatos.at(-2),
      };
    }));

    const headersTabla = computed(() => [
      { title: 'Mes', key: 'mes' },
      { title: 'Cargas', key: 'cargas', align: 'end' },
      { title: 'Galones', key: 'galones', align: 'end' },
      { title: 'Gasto', key: 'gasto', align: 'end' },
      ...datos.value.por_tipo.map((t) => ({ title: `${t.tipo} (S/ gal)`, key: `t${t.tipo_combustible_id}`, align: 'end' })),
    ]);

    const filasTabla = computed(() => [...datos.value.meses].reverse().map((m) => {
      const fila = {
        mes: nombreMes(m.clave),
        cargas: m.cargas,
        galones: galones(m.galones),
        gasto: soles(m.gasto),
      };
      datos.value.por_tipo.forEach((t) => {
        const p = t.serie.find((s) => s.clave === m.clave);
        fila[`t${t.tipo_combustible_id}`] = p?.precio_galon != null ? soles(p.precio_galon) : '—';
      });
      return fila;
    }));

    async function cargar() {
      cargando.value = true;
      error.value = null;
      try {
        const params = { meses: meses.value };
        if (autoId.value) params.auto_id = autoId.value;
        const { data } = await axios.get('/api/dashboard/combustible', { params });
        datos.value = data;
      } catch (e) {
        error.value = e.response?.data?.message || 'No se pudo cargar el dashboard de combustible.';
      } finally {
        cargando.value = false;
      }
    }

    function destruir() {
      raices.forEach((r) => r.dispose());
      raices = [];
    }

    function nuevoGrafico(el, { desdeCero, compacto = false }) {
      const t = TINTA[modo.value];
      const root = am5.Root.new(el);
      root.setThemes([am5themes_Animated.new(root)]);
      root.numberFormatter.set('numberFormat', '#,###.00');
      raices.push(root);

      const chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: false, panY: false, wheelX: 'none', wheelY: 'none',
        paddingLeft: 0, paddingRight: 10,
        layout: root.verticalLayout,
      }));
      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      cursor.lineY.set('visible', false);

      const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: compacto ? 70 : 40 });
      xRenderer.labels.template.setAll({ fill: am5.color(t.texto), fontSize: 12 });
      xRenderer.grid.template.set('visible', false);
      const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'mes', renderer: xRenderer }));

      const yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.labels.template.setAll({ fill: am5.color(t.texto), fontSize: 12 });
      yRenderer.grid.template.setAll({ stroke: am5.color(t.grid), strokeOpacity: 1 });
      // Barras siempre desde cero; en las líneas de precio la escala se ajusta para ver la tendencia.
      const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        renderer: yRenderer, numberFormat: "'S/ '#,###.##", extraMax: 0.1,
        ...(desdeCero ? { min: 0, strictMinMax: false } : { extraMin: 0.1 }),
      }));

      return { root, chart, xAxis, yAxis };
    }

    // Una gráfica por tipo (small multiples): cada una con su escala.
    function dibujarPrecio() {
      const t = TINTA[modo.value];
      datos.value.por_tipo.forEach((tp, i) => {
        const el = precioEls[tp.tipo_combustible_id];
        if (!(el instanceof HTMLElement)) return;
        const { root, chart, xAxis, yAxis } = nuevoGrafico(el, { desdeCero: false, compacto: true });

        // Sin cargas en un mes = hueco en la línea (no cero).
        const filas = datos.value.meses.map((m) => {
          const p = tp.serie.find((s) => s.clave === m.clave);
          return p?.precio_galon != null ? { mes: mesCorto(m.clave), precio: p.precio_galon } : { mes: mesCorto(m.clave) };
        });
        xAxis.data.setAll(filas);

        const color = am5.color(colorTipo(i));
        const series = chart.series.push(am5xy.LineSeries.new(root, {
          name: tp.tipo, xAxis, yAxis, valueYField: 'precio', categoryXField: 'mes',
          stroke: color, fill: color, connect: false,
          tooltip: am5.Tooltip.new(root, { labelText: "{categoryX}: S/ {valueY.formatNumber('#,###.00')} por galón" }),
        }));
        series.strokes.template.setAll({ strokeWidth: 2 });
        series.bullets.push(() => am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, { radius: 4, fill: color, stroke: am5.color(t.superficie), strokeWidth: 2 }),
        }));
        series.data.setAll(filas);
      });
    }

    function dibujarGasto() {
      if (!(gastoChart.value instanceof HTMLElement)) return;
      const { root, chart, xAxis, yAxis } = nuevoGrafico(gastoChart.value, { desdeCero: true });
      const filas = datos.value.meses.map((m, i, todos) => ({
        mes: mesCorto(m.clave),
        gasto: m.gasto,
        estado: i === todos.length - 1 ? ' (en curso)' : '',
      }));
      xAxis.data.setAll(filas);

      const color = am5.color(SERIES[modo.value][0]);
      const series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: 'Gasto', xAxis, yAxis, valueYField: 'gasto', categoryXField: 'mes', fill: color, stroke: color,
        tooltip: am5.Tooltip.new(root, { labelText: "{categoryX}{estado}: S/ {valueY.formatNumber('#,###.00')}" }),
      }));
      series.columns.template.setAll({
        width: am5.percent(60), cornerRadiusTL: 4, cornerRadiusTR: 4, strokeOpacity: 0,
      });
      // Mes en curso atenuado: todavía no está completo
      series.columns.template.adapters.add('fillOpacity', (op, target) =>
        (target.dataItem?.dataContext?.estado ? 0.4 : op));
      series.data.setAll(filas);
    }

    async function dibujar() {
      destruir();
      await nextTick();
      if (!hayDatos.value) return;
      dibujarPrecio();
      dibujarGasto();
    }

    watch([meses, autoId], cargar);
    watch([datos, isDark], dibujar, { flush: 'post' });

    onMounted(() => {
      cargar();
      if (!autos.value?.length) store.dispatch('gasto/fetchAutosForGasto');
    });
    onBeforeUnmount(destruir);

    return {
      periodos, meses, autoId, autos, cargando, error, hayDatos,
      mesActual, mesCerrado, mesPrevio, resumenTipos,
      headersTabla, filasTabla, precioEls, gastoChart,
      soles, galones, nombreMes,
    };
  },
});
</script>

<style scoped>
.amchart {
  width: 100%;
  height: 340px;
}

.amchart--mini {
  height: 240px;
}

.muestra {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.tabular :deep(td) {
  font-variant-numeric: tabular-nums;
}
</style>
